<template>
  <div
    class="bee-autocomplete"
    v-clickoutside="close"
    :aria-expanded="suggestionVisible"
  >
    <bee-input
      ref="input"
      v-bind="[$props, $attrs]"
      :placeholder="placeholder"
      :disabled="disabled"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
      @keydown.enter.native="handleKeyEnter"
      @keydown.native.tab="close"
    />
    <autocomplete-suggestion
      visible-arrow
      ref="suggestions"
      :placement="placement"
    >
      <li v-for="(item, index) in suggestions"
        :key="index"
        @click="select(item)"
      >
        <slot :item="item">
          {{item[valueKey]}}
        </slot>
      </li>
    </autocomplete-suggestion>

  </div>
</template>

<script>
import { debounce } from 'throttle-debounce'
import BeeInput from '../input'
import AutocompleteSuggestion from './autocomplete-suggestion.vue'
import Emitter from '../../utils/emitter'
import Clickoutside from '../../utils/clickoutside'

export default {
  name: 'BeeAutocomplete',
  mixins: [Emitter],
  directives: { Clickoutside },
  props: {
    valueKey: {
      type: String,
      default: 'value'
    },
    value: [String, Number],
    placeholder: String,
    disabled: Boolean,
    debounce: {
      type: Number,
      default: 300
    },
    placement: {
      type: String,
      default: 'bottom'
    },
    fetchSuggestions: Function,
    triggerOnFocus: {
      type: Boolean,
      default: true
    },
    selectWhenUnmatched: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activated: false,
      suggestions: [],
      highlightedIndex: -1,
      suggestionDisabled: false
    }
  },
  computed: {
    suggestionVisible() {
      const suggestions = this.suggestions
      let isValidData = Array.isArray(suggestions) && suggestions.length > 0
      return isValidData && this.activated
    }
  },
  components: {
    BeeInput,
    AutocompleteSuggestion
  },
  watch: {
    suggestionVisible(val) {
      let $input = this.getInput()
      if($input) {
        this.broadcast('BeeAutocompleteSuggestion', 'visible', [val])
      }
    }
  },
  methods: {
    select(item) {
      this.$emit('input', item[this.valueKey])
      this.$emit('select', item)
      this.$nextTick(() => {
        this.suggestions = []
        this.highlightedIndex = -1
      })
    },
    handleInput(value) {
      this.$emit('input', value)
      this.suggestionDisabled = false
      if(!this.triggerOnFocus && !value) {
        this.suggestionDisabled = true
        this.suggestions = []
        return
      }
      this.debouncedGetData(value)
    },
    handleFocus(event) {
      this.activated = true
      this.$emit('focus', event)
      if(this.triggerOnFocus) {
        this.debouncedGetData(this.value)
      }
    },
    handleBlur() {
      this.$emit('blur', event)
    },
    close() {
      this.activated = false
    },
    handleKeyEnter(event) {
      if(this.suggestionVisible && this.highlightedIndex >= 0 && this.highlightedIndex < this.suggestions.length) {
        event.preventDefault()
        this.select(this.suggestions[this.highlightedIndex])
      } else if(this.selectWhenUnmatched) {
        this.$emit('select', {value: this.value})
        this.$nextTick(() => {
          this.suggestions = []
          this.highlightedIndex = -1
        })
      }
    },
    getInput() {
      return this.$refs.input.getInput()
    },
    getData(queryString) {
      if(this.suggestionDisabled) {
        return
      }
      this.fetchSuggestions(queryString, (suggestions) => {
        if(this.suggestionDisabled) {
          return
        }
        if(Array.isArray(suggestions)) {
          this.suggestions = suggestions
        } else {
          console.error('[BeehiveUI Error][Autocomplete]autocomplete suggestions must be an array')
        }
      })
    }
  },
  mounted() {
    this.debouncedGetData = debounce(this.debounce, this.getData)
    this.$on('item-click', item => {
      this.select(item)
    })
  },
  beforeDestroy() {
    this.$refs.suggestions.$destroy()
  }
}
</script>



// WEBPACK FOOTER //
// src/components/autocomplete/autocomplete.vue